import { FC, useMemo, useState } from 'react'
import { ChevronDown, Loader2, MessageSquareOff, Search } from 'lucide-react'
import { WorkspaceItem } from './WorkspaceItem'
import { ScrollArea } from '../ui/scroll-area'
import { Skeleton } from '../ui/skeleton'
import { cn } from '../../lib/utils'
import type { WorkspaceSummary, WorkspaceType } from '../../services/workspaceService'

interface WorkspaceListProps {
  workspaces: WorkspaceSummary[]
  activeWorkspaceId: string | null
  searchTerm: string
  onWorkspaceSelect: (id: string) => void
  onRename?: (id: string, newName: string) => void
  onClose?: (id: string) => void
  onDelete?: (id: string) => void
  isLoading?: boolean
}

const typeOrder: WorkspaceType[] = ['admission', 'flow', 'chat']

const typeSectionLabels: Record<WorkspaceType, string> = {
  admission: 'Admissions',
  flow: 'Flows',
  chat: 'Chats',
}

/**
 * Grouped workspace list for the sidebar.
 * Groups workspaces by type (Admission / Flow / Chat), each collapsible.
 * Filters by search term across all groups.
 */
export const WorkspaceList: FC<WorkspaceListProps> = ({
  workspaces,
  activeWorkspaceId,
  searchTerm,
  onWorkspaceSelect,
  onRename,
  onClose,
  onDelete,
  isLoading,
}) => {
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({})

  const toggleGroup = (type: WorkspaceType) => {
    setCollapsedGroups(prev => ({ ...prev, [type]: !prev[type] }))
  }

  const grouped = useMemo(() => {
    const searchLower = searchTerm.toLowerCase()
    const filtered = workspaces.filter(
      ws => !ws.is_closed && ws.display_name.toLowerCase().includes(searchLower)
    )

    const groups: Record<WorkspaceType, WorkspaceSummary[]> = {
      admission: [],
      flow: [],
      chat: [],
    }

    for (const ws of filtered) {
      const bucket = groups[ws.type]
      if (bucket) bucket.push(ws)
    }

    // Sort each group by updated_at descending (most recent first)
    for (const type of typeOrder) {
      groups[type].sort(
        (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      )
    }

    return groups
  }, [workspaces, searchTerm])

  const totalFiltered = typeOrder.reduce((sum, t) => sum + grouped[t].length, 0)

  if (isLoading) {
    return (
      <ScrollArea className="flex-1 px-3">
        <div className="flex flex-col gap-1.5 py-2">
          {[...Array(5)].map((_, index) => (
            <div
              key={index}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-surface/50"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <Skeleton className="h-4 w-8 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-3.5 w-3/4" />
                <Skeleton className="h-2.5 w-1/3" />
              </div>
            </div>
          ))}
          <div className="flex items-center justify-center gap-2 py-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span>Loading workspaces...</span>
          </div>
        </div>
      </ScrollArea>
    )
  }

  if (totalFiltered === 0) {
    return (
      <ScrollArea className="flex-1 px-3">
        <div className="flex flex-col items-center justify-center text-center py-12 px-4">
          <div className="w-12 h-12 rounded-2xl bg-surface flex items-center justify-center mb-4">
            {searchTerm ? (
              <Search className="h-5 w-5 text-muted-foreground/60" />
            ) : (
              <MessageSquareOff className="h-5 w-5 text-muted-foreground/60" />
            )}
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            {searchTerm ? 'No matches found' : 'No workspaces yet'}
          </p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            {searchTerm
              ? 'Try a different search term'
              : 'Create a new workspace to get started'}
          </p>
        </div>
      </ScrollArea>
    )
  }

  return (
    // Radix ScrollArea wraps content in an inline `display:table; min-width:100%`
    // div, which grows to max-content — long workspace names then balloon each row
    // far wider than the panel, shoving the right-aligned kebab off the (clipped)
    // viewport. Forcing that wrapper to `block` pins rows to the viewport width so
    // names truncate and the kebab stays on screen. (!important beats Radix's inline style.)
    <ScrollArea className="flex-1 px-3 [&_[data-radix-scroll-area-viewport]>div]:!block">
      <div className="flex flex-col gap-1 py-2">
        {typeOrder.map(type => {
          const items = grouped[type]
          if (items.length === 0) return null

          const isCollapsed = collapsedGroups[type]

          return (
            <div key={type}>
              {/* Section header */}
              <button
                onClick={() => toggleGroup(type)}
                className={cn(
                  'flex items-center gap-1.5 w-full px-2 py-1.5',
                  'text-[11px] font-display font-semibold uppercase tracking-wider',
                  'text-muted-foreground/70 hover:text-muted-foreground',
                  'transition-colors'
                )}
              >
                <ChevronDown
                  className={cn(
                    'h-3 w-3 transition-transform duration-200',
                    isCollapsed && '-rotate-90'
                  )}
                />
                <span>{typeSectionLabels[type]}</span>
                <span className="text-[10px] text-muted-foreground/50 font-normal ml-auto">
                  {items.length}
                </span>
              </button>

              {/* Items */}
              {!isCollapsed &&
                items.map((ws, index) => (
                  <div
                    key={ws.id}
                    className="animate-fade-in"
                    style={{ animationDelay: `${index * 20}ms` }}
                  >
                    <WorkspaceItem
                      id={ws.id}
                      displayName={ws.display_name}
                      type={ws.type}
                      updatedAt={ws.updated_at}
                      isActive={ws.id === activeWorkspaceId}
                      onClick={() => onWorkspaceSelect(ws.id)}
                      onRename={onRename}
                      onClose={onClose}
                      onDelete={onDelete}
                    />
                  </div>
                ))}
            </div>
          )
        })}
      </div>
    </ScrollArea>
  )
}
